import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Select from "react-select";
import { FetchSetting, SaveFeeSettings } from "./PaymentSlice";
import { ClassListApi } from "../../api/class";
import { FetchCompanySettings } from "../slice";

function FeeSettings() {
  const dispatch = useDispatch();
  const settingData = useSelector((state) => state?.payment?.setting);
  const authState = useSelector((state) => state.auth);
  const subAdminData = useSelector((state) => state.subAdmin.detail);
  const venues = settingData?.venue_hall_fees || [];
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState({
    sectionName: '',
    action: '',
    onConfirm: null
  });

  // Confirmation helper function
  const confirmToggleAction = (sectionName, isEnabling, onConfirm) => {
    const action = isEnabling ? "enable" : "disable";
    setConfirmModalData({
      sectionName,
      action,
      onConfirm
    });
    setShowConfirmModal(true);
  };

  const handleConfirmYes = () => {
    if (confirmModalData.onConfirm) {
      confirmModalData.onConfirm();
    }
    setShowConfirmModal(false);
  };

  const handleConfirmNo = () => {
    setShowConfirmModal(false);
  };

  let companyOptions = [];
    const companiesStr = localStorage.getItem('companies');
    if (companiesStr) {
      try {
        const companies = JSON.parse(companiesStr);
        if (Array.isArray(companies) && companies.length > 0) {
          companyOptions = [
            ...companies.map(c => ({
              value: c.id,
              label: c.company_name
            }))
          ];
        }
      } catch (e) {
        // Optionally handle JSON parse error
        companyOptions = [];
      }
    }
    const [selectedCompany, setSelectedCompany] = useState('');

  // Hall Fee States
  const [hallFeeEnabled, setHallFeeEnabled] = useState(false);
  const [hallFeeType, setHallFeeType] = useState("global"); // global or venue
  const [hallFeeGlobalPerHour, setHallFeeGlobalPerHour] = useState("");
  const [hallFeeGlobalPerDay, setHallFeeGlobalPerDay] = useState("");
  const [hallFeeVenueValues, setHallFeeVenueValues] = useState({});

  // Franchise Fee States
  const [franchiseFeeEnabled, setFranchiseFeeEnabled] = useState(false);
  const [franchiseFeeType, setFranchiseFeeType] = useState("global"); // global or per_session
  const [studentThreshold, setStudentThreshold] = useState("");
  const [franchiseFeeValue, setFranchiseFeeValue] = useState("");
  const [franchiseFeeClassValues, setFranchiseFeeClassValues] = useState({});
  const [franchiseFeeClassList, setFranchiseFeeClassList] = useState([]);

  // Admin Commission States
  const [adminCommissionEnabled, setAdminCommissionEnabled] = useState(false);
  const [adminCommissionValue, setAdminCommissionValue] = useState("");

  // Trial Lesson States
  const [trialLessonEnabled, setTrialLessonEnabled] = useState(false);
  const [trialType, setTrialType] = useState("per_class"); // global or per_class
  const [trialNumber, setTrialNumber] = useState("");
  const [trialFee, setTrialFee] = useState("");
  const [trialClassValues, setTrialClassValues] = useState({});
  const [trialClassList, setTrialClassList] = useState([]);

  const toBoolean = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") return value === "1" || value.toLowerCase() === "true";
    return false;
  };

  const deriveClassTrialToggle = (classItem) => {
    if (!classItem) return false;
    if (typeof classItem.free_class_available !== "undefined") {
      return toBoolean(classItem.free_class_available);
    }
    const noOfFreeClass = Number(classItem.no_of_free_class || 0);
    const freeClassCharge = Number(classItem.free_class_charge || 0);
    return noOfFreeClass > 0 || freeClassCharge > 0;
  };

  // Late Booking States
  const [lateBookingEnabled, setLateBookingEnabled] = useState(false);
  const [lateBookingChargeType, setLateBookingChargeType] = useState("global"); // global or per_class
  const [lateBookingHours, setLateBookingHours] = useState("");
  const [lateBookingCharge, setLateBookingCharge] = useState("");
  const [lateBookingClassValues, setLateBookingClassValues] = useState({});
  const [classList, setClassList] = useState([]);

  // Allow Free Membership State
  const [allowFreeMembership, setAllowFreeMembership] = useState(false);
  const [allowFreeMembershipInner, setAllowFreeMembershipInner] = useState(false);

  // Events State
  const [eventsEnabled, setEventsEnabled] = useState(false);
  const [eventsInnerEnabled, setEventsInnerEnabled] = useState(false);

  // Finance State
  const [financeEnabled, setFinanceEnabled] = useState(false);
  const [financeInnerEnabled, setFinanceInnerEnabled] = useState(false);

  // Collapse States for all sections
  const [hallFeeCollapsed, setHallFeeCollapsed] = useState(true);
  const [franchiseFeeCollapsed, setFranchiseFeeCollapsed] = useState(true);
  const [adminCommissionCollapsed, setAdminCommissionCollapsed] = useState(true);
  const [trialLessonCollapsed, setTrialLessonCollapsed] = useState(true);
  const [lateBookingCollapsed, setLateBookingCollapsed] = useState(true);
  const [freeMembershipCollapsed, setFreeMembershipCollapsed] = useState(true);
  const [eventsCollapsed, setEventsCollapsed] = useState(true);
  const [financeCollapsed, setFinanceCollapsed] = useState(true);

  // Inner section enable states
  const [hallFeeInnerEnabled, setHallFeeInnerEnabled] = useState(false);
  const [franchiseFeeInnerEnabled, setFranchiseFeeInnerEnabled] = useState(false);
  const [adminCommissionInnerEnabled, setAdminCommissionInnerEnabled] = useState(false);
  const [trialLessonInnerEnabled, setTrialLessonInnerEnabled] = useState(false);
  const [lateBookingInnerEnabled, setLateBookingInnerEnabled] = useState(false);

  // Check if toggle should be disabled (when company options exist but none selected)
  const isToggleDisabled = companyOptions.length === 0;

  // When outer toggles turn off, turn off inner toggles
  useEffect(() => {
    if (!trialLessonEnabled) setTrialLessonInnerEnabled(false);
  }, [trialLessonEnabled]);

  useEffect(() => {
    if (!allowFreeMembership) setAllowFreeMembershipInner(false);
  }, [allowFreeMembership]);

  useEffect(() => {
    if (!adminCommissionEnabled) setAdminCommissionInnerEnabled(false);
  }, [adminCommissionEnabled]);

  useEffect(() => {
    if (!lateBookingEnabled) setLateBookingInnerEnabled(false);
  }, [lateBookingEnabled]);

  useEffect(() => {
    if (!hallFeeEnabled) setHallFeeInnerEnabled(false);
  }, [hallFeeEnabled]);

  useEffect(() => {
    if (!franchiseFeeEnabled) setFranchiseFeeInnerEnabled(false);
  }, [franchiseFeeEnabled]);

  useEffect(() => {
    if (!eventsEnabled) setEventsInnerEnabled(false);
  }, [eventsEnabled]);

  useEffect(() => {
    if (!financeEnabled) setFinanceInnerEnabled(false);
  }, [financeEnabled]);

  useEffect(() => {
    console.log('=== FeeSettings Component - Fetching Settings ===');
    console.log('companyOptions.length:', companyOptions.length);
    console.log('selectedCompany:', selectedCompany);
    console.log('is_company:', subAdminData?.is_company);
    
    // Check if the logged-in user is a company user
    const isCompanyUser = subAdminData?.is_company || authState?.user_type?.toLowerCase() === 'company';
    
    // Reset subscription when company changes to prevent stale data
    setSubscription(null);
    
    if (isCompanyUser) {
      // Company user: fetch settings for their own company
      const companyId = subAdminData?.id || authState?.company_id;
      if (companyId) {
        setLoading(true);
        console.log('Company user detected, fetching settings for company_id:', companyId);
        dispatch(FetchSetting({ company_id: companyId }))
          .then(() => setLoading(false));
      }
    } else if (companyOptions.length === 0) {
      // Super admin with no company options, fetch settings directly without company_id
      setLoading(true);
      dispatch(FetchSetting())
        .then(() => setLoading(false));
    } else if (selectedCompany) {
      // Super admin with company selected
      setLoading(true);
      console.log('Fetching settings for company_id:', selectedCompany.value);
      dispatch(FetchSetting({ company_id: selectedCompany.value }))
        .then(() => setLoading(false));
    }
  }, [dispatch, selectedCompany, companyOptions.length, subAdminData?.id, authState?.company_id, subAdminData?.is_company, authState?.user_type]);

  // Helper function to check if the company is in a trial period
  const isInTrialPeriod = () => {
    const trialEnd = settingData?.trial_end || subAdminData?.trial_end || authState?.trial_end;
    if (!trialEnd) return false;
    const trialEndDate = new Date(trialEnd);
    const currentDate = new Date();
    return currentDate <= trialEndDate;
  };

  // Helper function to check if a section should be displayed based on addon codes
  const shouldDisplaySection = (requiredCode) => {
    // Check if the logged-in user is a company user
    const isCompanyUser = subAdminData?.is_company || authState?.user_type?.toLowerCase() === 'company';

    // If company is in trial period, allow all add-on management sections
    if (isInTrialPeriod()) {
      return true;
    }
    
    // If user is super admin (not company) and no company selected, show all sections
    if (!isCompanyUser && !selectedCompany) {
      return true;
    }
    
    // If company is selected or user is company, need to check subscription
    if (selectedCompany || isCompanyUser) {
      // If no subscription data, hide all sections
      if (!subscription) {
        return false;
      }
      
      // Check if the required addon code exists in the subscription
      // Structure: subscription.addons[].addon.code
      if (!subscription.addons || !Array.isArray(subscription.addons)) {
        return false;
      }
      
      console.log('=== Debugging Addon Codes ===');
      console.log('Required code:', requiredCode);
      console.log('Subscription addons:', subscription.addons);
      console.log('Is company user:', isCompanyUser);
      subscription.addons.forEach((item, index) => {
        console.log(`Addon ${index}:`, item);
        console.log(`Addon ${index} code:`, item.addon?.code);
      });
      
      const hasAddon = subscription.addons.some(item => item.addon?.code === requiredCode);

      // Finance section can also be enabled directly via company settings flags
      if (!hasAddon && requiredCode === 'finance') {
        const s = settingData?.settings || settingData || {};
        return !!(s.admin_finance || s.finance);
      }

      return hasAddon;
    }
    
    return true;
  };

  useEffect(() => {
    if (settingData) {
      console.log('=== Settings Data Received ===');
      console.log('Full settingData:', settingData);
      console.log('settingData.subscription:', settingData.subscription);
      
      // Store subscription data if available
      if (settingData.subscription) {
        console.log('Setting subscription state with:', settingData.subscription);
        setSubscription(settingData.subscription);
      } else {
        console.log('No subscription data in settingData');
      }
      
      // Access settings from settingData.settings since we now dispatch the entire data object
      const settings = settingData.settings || settingData;
      
      // Load Hall Fee settings
      setHallFeeEnabled(settings.admin_hall_fee_apply || false);
      setHallFeeType(settings.hall_fee_type || "global");
      setHallFeeGlobalPerHour(settings.hall_fee_global_per_hour || "");
      setHallFeeGlobalPerDay(settings.hall_fee_global_per_day || "");
      setHallFeeInnerEnabled(settings.hall_fee_apply || false);

      // Load Franchise Fee settings
      setFranchiseFeeEnabled(settings.admin_franchise_fee_apply || false);
      setFranchiseFeeType(settings.franchise_fee_type || "global");
      setStudentThreshold(settings.student_threshold || "");
      setFranchiseFeeValue(settings.franchise_fee || "");
      setFranchiseFeeInnerEnabled(settings.franchise_fee_apply || false);

      // Load Admin Commission settings
      setAdminCommissionEnabled(settings.admin_admin_commision_apply || false);
      setAdminCommissionValue(settings.admin_commision || "");
      setAdminCommissionInnerEnabled(settings.admin_commision_apply || false);

      // Load Trial Lesson settings
      setTrialLessonEnabled(settings.admin_trial_apply || false);
      setTrialType(settings.trial_type || "per_class");
      setTrialNumber(settings.trial_number || "");
      setTrialFee(settings.trial_fee || "");
      setTrialLessonInnerEnabled(settings.trial_apply || false);

      // Load Late Booking settings
      setLateBookingEnabled(settings.admin_late_booking_charge_apply || false);
      setLateBookingChargeType(settings.late_booking_charge_type || "global");
      setLateBookingHours(settings.late_booking_hours || "");
      setLateBookingCharge(settings.late_booking_charge || "");
      setLateBookingInnerEnabled(settings.late_booking_charge_apply || false);

      // Load Allow Free Membership setting
      setAllowFreeMembership(settings.admin_membership || false);
      setAllowFreeMembershipInner(settings.membership || false);

      // Load Events setting
      setEventsEnabled(settings.admin_events || false);
      setEventsInnerEnabled(settings.events || false);

      // Load Finance setting
      setFinanceEnabled(settings.admin_finance || false);
      setFinanceInnerEnabled(settings.finance || false);
    }
  }, [settingData]);

  useEffect(() => {
    if (venues && venues.length > 0) {
      // Initialize venue values from venues data
      const initialVenueValues = {};
      venues.forEach(venue => {
        initialVenueValues[venue.id] = {
          per_hour: venue.hall_fees_per_hour || "",
          per_day: venue.hall_fees_per_day || ""
        };
      });
      setHallFeeVenueValues(initialVenueValues);
    }
  }, [venues]);

  // Fetch class list when late booking is enabled and per_class type is selected
  useEffect(() => {
    if (lateBookingEnabled && lateBookingChargeType === "per_class") {
      const params = { status: 1 };
      if (selectedCompany?.value && selectedCompany.value > 0) {
        params.company_id = selectedCompany.value;
      }
      ClassListApi(params)
        .then((response) => {
          if (response?.code === 1 && response?.data?.class_list) {
            setClassList(response.data.class_list);
          }
        })
        .catch((error) => {
          console.error("Error fetching class list:", error);
        });
    }
  }, [lateBookingEnabled, lateBookingChargeType, selectedCompany]);

  // Fetch class list when franchise fee is enabled and per_session type is selected
  useEffect(() => {
    if (franchiseFeeEnabled && franchiseFeeType === "per_session") {
      const params = { status: 1 };
      if (selectedCompany?.value && selectedCompany.value > 0) {
        params.company_id = selectedCompany.value;
      }
      ClassListApi(params)
        .then((response) => {
          if (response?.code === 1 && response?.data?.class_list) {
            setFranchiseFeeClassList(response.data.class_list);
          }
        })
        .catch((error) => {
          console.error("Error fetching class list:", error);
        });
    }
  }, [franchiseFeeEnabled, franchiseFeeType, selectedCompany]);

  // Initialize franchise fee class values from franchiseFeeClassList
  useEffect(() => {
    if (franchiseFeeClassList && franchiseFeeClassList.length > 0) {
      const initialClassValues = {};
      franchiseFeeClassList.forEach(classItem => {
        initialClassValues[classItem.id] = {
          student_threshold: classItem.student_threshold || "",
          franchise_fee: classItem.franchise_fee || ""
        };
      });
      setFranchiseFeeClassValues(initialClassValues);
    }
  }, [franchiseFeeClassList]);

  // Initialize late booking class values from classList
  useEffect(() => {
    if (classList && classList.length > 0) {
      const initialClassValues = {};
      classList.forEach(classItem => {
        initialClassValues[classItem.id] = {
          late_booking_hours: classItem.late_booking_hours || "",
          late_booking_charge: classItem.late_booking_charge || ""
        };
      });
      setLateBookingClassValues(initialClassValues);
    }
  }, [classList]);

  // Fetch class list when trial lesson is enabled and per_class type is selected
  useEffect(() => {
    if (trialType !== "per_class") {
      setTrialClassList([]);
      return;
    }
    const params = { status: 1 };
    if (selectedCompany?.value && selectedCompany.value > 0) {
      params.company_id = selectedCompany.value;
    }
    ClassListApi(params)
      .then((response) => {
        if (response?.code === 1 && response?.data?.class_list) {
          setTrialClassList(response.data.class_list);
        } else {
          setTrialClassList([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching class list for trial:", error);
        setTrialClassList([]);
      });
  }, [trialType, selectedCompany]);

  // Initialize trial lesson class values from trialClassList
  useEffect(() => {
    if (trialClassList && trialClassList.length > 0) {
      const initialClassValues = {};
      trialClassList.forEach(classItem => {
        initialClassValues[classItem.id] = {
          no_of_free_class: classItem.no_of_free_class || "",
          free_class_booking_fee: classItem.free_class_booking_fee || "",
          child_price: classItem.child_price || "",
          adult_price: classItem.adult_price || "",
          enabled: deriveClassTrialToggle(classItem)
        };
      });
      setTrialClassValues(initialClassValues);
    } else {
      setTrialClassValues({});
    }
  }, [trialClassList]);

  const handleVenueValueChange = (venueId, type, value) => {
    setHallFeeVenueValues(prev => ({
      ...prev,
      [venueId]: {
        ...prev[venueId],
        [type]: value
      }
    }));
  };

  const handleLateBookingClassValueChange = (classId, type, value) => {
    setLateBookingClassValues(prev => ({
      ...prev,
      [classId]: {
        ...prev[classId],
        [type]: value
      }
    }));
  };

  const handleTrialClassValueChange = (classId, type, value) => {
    setTrialClassValues(prev => ({
      ...prev,
      [classId]: {
        ...prev[classId],
        [type]: value
      }
    }));
  };

  const handleTrialClassToggle = (classId, isEnabled) => {
    setTrialClassValues(prev => ({
      ...prev,
      [classId]: {
        ...prev[classId],
        enabled: isEnabled
      }
    }));
  };

  const handleFranchiseFeeClassValueChange = (classId, type, value) => {
    setFranchiseFeeClassValues(prev => ({
      ...prev,
      [classId]: {
        ...prev[classId],
        [type]: value
      }
    }));
  };

  const handleHallFeeToggle = (checked) => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    // Show confirmation dialog
    confirmToggleAction("Hall Fee", checked, () => {
      setHallFeeEnabled(checked);

      const payload = {
        setting_type: "hall_fee",
        enabled: checked,
        hall_fee_type: hallFeeType,
        hall_fee_global_per_hour: hallFeeGlobalPerHour,
        hall_fee_global_per_day: hallFeeGlobalPerDay,
        hall_fee_venue_values: JSON.stringify(hallFeeVenueValues)
      };

      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }

      dispatch(SaveFeeSettings(payload)).then((response) => {
        if (response?.payload?.code === 1) {
          // Only refresh company settings for company admins (not super admins)
          // Super admins have companyOptions, company admins don't
          if (companyOptions.length === 0) {
            dispatch(FetchCompanySettings());
          }
          // Refresh fee settings data
          const companyId = selectedCompany?.value;
          if (companyId) {
            dispatch(FetchSetting({ company_id: companyId }));
          } else {
            dispatch(FetchSetting());
          }
        }
      });
    });
  };

  const handleFranchiseFeeToggle = (checked) => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    // Show confirmation dialog
    confirmToggleAction("Franchise Fee", checked, () => {
      setFranchiseFeeEnabled(checked);

      const payload = {
        setting_type: "franchise_fee",
        enabled: checked,
        franchise_fee_type: franchiseFeeType,
        student_threshold: studentThreshold,
        franchise_fee_value: franchiseFeeValue,
        franchise_fee_class_values: JSON.stringify(franchiseFeeClassValues)
      };

      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }

      dispatch(SaveFeeSettings(payload)).then((response) => {
        if (response?.payload?.code === 1) {
          // Only refresh company settings for company admins (not super admins)
          // Super admins have companyOptions, company admins don't
          if (companyOptions.length === 0) {
            dispatch(FetchCompanySettings());
          }
          // Refresh fee settings data
          const companyId = selectedCompany?.value;
          if (companyId) {
            dispatch(FetchSetting({ company_id: companyId }));
          } else {
            dispatch(FetchSetting());
          }
        }
      });
    });
  };

  const handleAdminCommissionToggle = (checked) => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    // Show confirmation dialog
    confirmToggleAction("Company Charges", checked, () => {
      setAdminCommissionEnabled(checked);

      const payload = {
        setting_type: "admin_commission",
        enabled: checked,
        admin_commission_value: adminCommissionValue
      };

      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }

      dispatch(SaveFeeSettings(payload)).then((response) => {
        if (response?.payload?.code === 1) {
          // Only refresh company settings for company admins (not super admins)
          // Super admins have companyOptions, company admins don't
          if (companyOptions.length === 0) {
            dispatch(FetchCompanySettings());
          }
          // Refresh fee settings data
          const companyId = selectedCompany?.value;
          if (companyId) {
            dispatch(FetchSetting({ company_id: companyId }));
          } else {
            dispatch(FetchSetting());
          }
        }
      });
    });
  };

  const handleSaveHallFee = () => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    const payload = {
      setting_type: "hall_fee",
      enabled: hallFeeEnabled,
      hall_fee_type: hallFeeType,
      hall_fee_global_per_hour: hallFeeGlobalPerHour,
      hall_fee_global_per_day: hallFeeGlobalPerDay,
      hall_fee_venue_values: JSON.stringify(hallFeeVenueValues)
    };

    if (selectedCompany) {
      payload.company_id = selectedCompany.value;
    }

    dispatch(SaveFeeSettings(payload)).then((response) => {
      if (response?.payload?.code === 1) {
        //window.location.reload();
      }
    });
  };

  const handleSaveFranchiseFee = () => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    const payload = {
      setting_type: "franchise_fee",
      enabled: franchiseFeeEnabled,
      franchise_fee_type: franchiseFeeType,
      student_threshold: studentThreshold,
      franchise_fee_value: franchiseFeeValue,
      franchise_fee_class_values: JSON.stringify(franchiseFeeClassValues)
    };

    if (selectedCompany) {
      payload.company_id = selectedCompany.value;
    }

    dispatch(SaveFeeSettings(payload)).then((response) => {
      if (response?.payload?.code === 1) {
        //window.location.reload();
      }
    });
  };

  const handleSaveAdminCommission = () => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    const payload = {
      setting_type: "admin_commission",
      enabled: adminCommissionEnabled,
      admin_commission_value: adminCommissionValue
    };

    if (selectedCompany) {
      payload.company_id = selectedCompany.value;
    }

    dispatch(SaveFeeSettings(payload)).then((response) => {
      if (response?.payload?.code === 1) {
        //window.location.reload();
      }
    });
  };

  const buildTrialClassValuesPayload = () => {
    const payload = {};
    (trialClassList || []).forEach((classItem) => {
      const settings = trialClassValues[classItem.id] || {};
      const childPrice = settings.child_price !== undefined && settings.child_price !== ""
        ? Number(settings.child_price)
        : Number(classItem.child_price || 0);
      const adultPrice = settings.adult_price !== undefined && settings.adult_price !== ""
        ? Number(settings.adult_price)
        : Number(classItem.adult_price || 0);
      const bookingFee = Number(settings.free_class_booking_fee || 0);
      const childTotal = childPrice + bookingFee;
      const adultTotal = adultPrice + bookingFee;
      payload[classItem.id] = {
        ...settings,
        no_of_free_class: settings.no_of_free_class || "",
        child_price: childPrice.toFixed(2),
        adult_price: adultPrice.toFixed(2),
        free_class_booking_fee: settings.free_class_booking_fee || "",
        free_class_charge: childTotal.toFixed(2),
        free_class_charge_adult: adultTotal.toFixed(2)
      };
    });
    return payload;
  };

  const handleSaveTrialLesson = () => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    const perClassEnabled = Object.values(trialClassValues || {}).some(
      (value) => value?.enabled
    );
    const isTrialApply = trialType === "per_class"
      ? perClassEnabled
      : Boolean(trialNumber || trialFee);

    const payload = {
      setting_type: "trial_lesson",
      enabled: isTrialApply,
      trial_type: trialType,
      trial_number: trialNumber,
      trial_fee: trialFee,
      trial_class_values: JSON.stringify(buildTrialClassValuesPayload())
    };

    if (selectedCompany) {
      payload.company_id = selectedCompany.value;
    }

    dispatch(SaveFeeSettings(payload)).then((response) => {
      if (response?.payload?.code === 1) {
        //window.location.reload();
      }
    });
  };

  const handleLateBookingToggle = (checked) => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    // Show confirmation dialog
    confirmToggleAction("Late Booking", checked, () => {
      setLateBookingEnabled(checked);

      const payload = {
        setting_type: "late_booking",
        enabled: checked,
        late_booking_charge_type: lateBookingChargeType,
        late_booking_hours: lateBookingHours,
        late_booking_charge: lateBookingCharge,
        late_booking_class_values: JSON.stringify(lateBookingClassValues)
      };

      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }

      dispatch(SaveFeeSettings(payload)).then((response) => {
        if (response?.payload?.code === 1) {
          // Only refresh company settings for company admins (not super admins)
          // Super admins have companyOptions, company admins don't
          if (companyOptions.length === 0) {
            dispatch(FetchCompanySettings());
          }
          // Refresh fee settings data
          const companyId = selectedCompany?.value;
          if (companyId) {
            dispatch(FetchSetting({ company_id: companyId }));
          } else {
            dispatch(FetchSetting());
          }
        }
      });
    });
  };

  const handleSaveLateBooking = () => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    const payload = {
      setting_type: "late_booking",
      enabled: lateBookingEnabled,
      late_booking_charge_type: lateBookingChargeType,
      late_booking_hours: lateBookingHours,
      late_booking_charge: lateBookingCharge,
      late_booking_class_values: JSON.stringify(lateBookingClassValues)
    };

    if (selectedCompany) {
      payload.company_id = selectedCompany.value;
    }

    dispatch(SaveFeeSettings(payload)).then((response) => {
      if (response?.payload?.code === 1) {
        //window.location.reload();
      }
    });
  };

  const handleFreeMembershipToggle = (checked) => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    // Show confirmation dialog
    confirmToggleAction("Free Membership", checked, () => {
      setAllowFreeMembership(checked);

      const payload = {
        setting_type: "free_membership",
        enabled: checked
      };

      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }

      dispatch(SaveFeeSettings(payload)).then((response) => {
        if (response?.payload?.code === 1) {
          // Only refresh company settings for company admins (not super admins)
          // Super admins have companyOptions, company admins don't
          if (companyOptions.length === 0) {
            dispatch(FetchCompanySettings());
          }
          // Refresh fee settings data
          const companyId = selectedCompany?.value;
          if (companyId) {
            dispatch(FetchSetting({ company_id: companyId }));
          } else {
            dispatch(FetchSetting());
          }
        }
      });
    });
  };

  const handleFreeMembershipInnerToggle = (checked) => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    // Show confirmation dialog
    confirmToggleAction("Free Membership", checked, () => {
      setAllowFreeMembershipInner(checked);

      const payload = {
        setting_type: "free_membership",
        company_enabled: checked
      };

      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }

      dispatch(SaveFeeSettings(payload)).then((response) => {
        if (response?.payload?.code === 1) {
          // Only refresh company settings for company admins (not super admins)
          // Super admins have companyOptions, company admins don't
          if (companyOptions.length === 0) {
            dispatch(FetchCompanySettings());
          }
          // Refresh fee settings data
          const companyId = selectedCompany?.value;
          if (companyId) {
            dispatch(FetchSetting({ company_id: companyId }));
          } else {
            dispatch(FetchSetting());
          }
        }
      });
    });
  };

  const handleEventsToggle = (checked) => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    // Show confirmation dialog
    confirmToggleAction("Events", checked, () => {
      setEventsEnabled(checked);

      const payload = {
        setting_type: "events",
        enabled: checked
      };

      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }

      dispatch(SaveFeeSettings(payload)).then((response) => {
        if (response?.payload?.code === 1) {
          // Only refresh company settings for company admins (not super admins)
          // Super admins have companyOptions, company admins don't
          if (companyOptions.length === 0) {
            dispatch(FetchCompanySettings());
          }
          // Refresh fee settings data
          const companyId = selectedCompany?.value;
          if (companyId) {
            dispatch(FetchSetting({ company_id: companyId }));
          } else {
            dispatch(FetchSetting());
          }
        }
      });
    });
  };

  const handleFinanceToggle = (checked) => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    // Show confirmation dialog
    confirmToggleAction("Finance", checked, () => {
      setFinanceEnabled(checked);

      const payload = {
        setting_type: "finance",
        enabled: checked
      };

      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }

      dispatch(SaveFeeSettings(payload)).then((response) => {
        if (response?.payload?.code === 1) {
          // Only refresh company settings for company admins (not super admins)
          // Super admins have companyOptions, company admins don't
          if (companyOptions.length === 0) {
            dispatch(FetchCompanySettings());
          }
          // Refresh fee settings data
          const companyId = selectedCompany?.value;
          if (companyId) {
            dispatch(FetchSetting({ company_id: companyId }));
          } else {
            dispatch(FetchSetting());
          }
        }
      });
    });
  };

  // Inner toggle handlers that send company_enabled parameter
  const handleTrialLessonInnerToggle = (checked) => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    confirmToggleAction("Tester Session", checked, () => {
      setTrialLessonInnerEnabled(checked);

      const payload = {
        setting_type: "trial_lesson",
        company_enabled: checked,
        trial_type: trialType,
        trial_number: trialNumber,
        trial_fee: trialFee,
        trial_class_values: JSON.stringify(buildTrialClassValuesPayload())
      };

      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }

      dispatch(SaveFeeSettings(payload)).then((response) => {
        if (response?.payload?.code === 1) {
          // Only refresh company settings for company admins (not super admins)
          // Super admins have companyOptions, company admins don't
          if (companyOptions.length === 0) {
            dispatch(FetchCompanySettings());
          }
          // Refresh fee settings data
          const companyId = selectedCompany?.value;
          if (companyId) {
            dispatch(FetchSetting({ company_id: companyId }));
          } else {
            dispatch(FetchSetting());
          }
        }
      });
    });
  };

  const handleAdminCommissionInnerToggle = (checked) => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    confirmToggleAction("Admin Commission", checked, () => {
      setAdminCommissionInnerEnabled(checked);

      const payload = {
        setting_type: "admin_commission",
        company_enabled: checked,
        admin_commission_value: adminCommissionValue
      };

      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }

      dispatch(SaveFeeSettings(payload)).then((response) => {
        if (response?.payload?.code === 1) {
          // Only refresh company settings for company admins (not super admins)
          // Super admins have companyOptions, company admins don't
          if (companyOptions.length === 0) {
            dispatch(FetchCompanySettings());
          }
          // Refresh fee settings data
          const companyId = selectedCompany?.value;
          if (companyId) {
            dispatch(FetchSetting({ company_id: companyId }));
          } else {
            dispatch(FetchSetting());
          }
        }
      });
    });
  };

  const handleLateBookingInnerToggle = (checked) => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    confirmToggleAction("Late Booking", checked, () => {
      setLateBookingInnerEnabled(checked);

      const payload = {
        setting_type: "late_booking",
        company_enabled: checked,
        late_booking_charge_type: lateBookingChargeType,
        late_booking_hours: lateBookingHours,
        late_booking_charge: lateBookingCharge,
        late_booking_class_values: JSON.stringify(lateBookingClassValues)
      };

      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }

      dispatch(SaveFeeSettings(payload)).then((response) => {
        if (response?.payload?.code === 1) {
          // Only refresh company settings for company admins (not super admins)
          // Super admins have companyOptions, company admins don't
          if (companyOptions.length === 0) {
            dispatch(FetchCompanySettings());
          }
          // Refresh fee settings data
          const companyId = selectedCompany?.value;
          if (companyId) {
            dispatch(FetchSetting({ company_id: companyId }));
          } else {
            dispatch(FetchSetting());
          }
        }
      });
    });
  };

  const handleHallFeeInnerToggle = (checked) => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    confirmToggleAction("Hall Fee", checked, () => {
      setHallFeeInnerEnabled(checked);

      const payload = {
        setting_type: "hall_fee",
        company_enabled: checked,
        hall_fee_type: hallFeeType,
        hall_fee_global_per_hour: hallFeeGlobalPerHour,
        hall_fee_global_per_day: hallFeeGlobalPerDay,
        hall_fee_venue_values: JSON.stringify(hallFeeVenueValues)
      };

      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }

      dispatch(SaveFeeSettings(payload)).then((response) => {
        if (response?.payload?.code === 1) {
          // Only refresh company settings for company admins (not super admins)
          // Super admins have companyOptions, company admins don't
          if (companyOptions.length === 0) {
            dispatch(FetchCompanySettings());
          }
          // Refresh fee settings data
          const companyId = selectedCompany?.value;
          if (companyId) {
            dispatch(FetchSetting({ company_id: companyId }));
          } else {
            dispatch(FetchSetting());
          }
        }
      });
    });
  };

  const handleFranchiseFeeInnerToggle = (checked) => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    confirmToggleAction("Franchise Fee", checked, () => {
      setFranchiseFeeInnerEnabled(checked);

      const payload = {
        setting_type: "franchise_fee",
        company_enabled: checked,
        franchise_fee_value: franchiseFeeValue,
        student_threshold: studentThreshold
      };

      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }

      dispatch(SaveFeeSettings(payload)).then((response) => {
        if (response?.payload?.code === 1) {
          // Only refresh company settings for company admins (not super admins)
          // Super admins have companyOptions, company admins don't
          if (companyOptions.length === 0) {
            dispatch(FetchCompanySettings());
          }
          // Refresh fee settings data
          const companyId = selectedCompany?.value;
          if (companyId) {
            dispatch(FetchSetting({ company_id: companyId }));
          } else {
            dispatch(FetchSetting());
          }
        }
      });
    });
  };

  const handleEventsInnerToggle = (checked) => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    confirmToggleAction("Events", checked, () => {
      setEventsInnerEnabled(checked);

      const payload = {
        setting_type: "events",
        company_enabled: checked
      };

      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }

      dispatch(SaveFeeSettings(payload)).then((response) => {
        if (response?.payload?.code === 1) {
          // Only refresh company settings for company admins (not super admins)
          // Super admins have companyOptions, company admins don't
          if (companyOptions.length === 0) {
            dispatch(FetchCompanySettings());
          }
          // Refresh fee settings data
          const companyId = selectedCompany?.value;
          if (companyId) {
            dispatch(FetchSetting({ company_id: companyId }));
          } else {
            dispatch(FetchSetting());
          }
        }
      });
    });
  };

  const handleFinanceInnerToggle = (checked) => {
    if (companyOptions.length > 0 && !selectedCompany) {
      toast.error("Please select a company", {  autoClose: false,  closeButton: true});
      return;
    }

    confirmToggleAction("Finance", checked, () => {
      setFinanceInnerEnabled(checked);

      const payload = {
        setting_type: "finance",
        company_enabled: checked
      };

      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }

      dispatch(SaveFeeSettings(payload)).then((response) => {
        if (response?.payload?.code === 1) {
          // Only refresh company settings for company admins (not super admins)
          // Super admins have companyOptions, company admins don't
          if (companyOptions.length === 0) {
            dispatch(FetchCompanySettings());
          }
          // Refresh fee settings data
          const companyId = selectedCompany?.value;
          if (companyId) {
            dispatch(FetchSetting({ company_id: companyId }));
          } else {
            dispatch(FetchSetting());
          }
        }
      });
    });
  };

  return (
    <article className="col-lg-9">
      <div className="article-body store-bd-gy">
        <div className="row mb-4">
          <div className="col-md-12">
            <h4>Add-On Management</h4>
          </div>
        </div>

        {/* Company Selection - Only show if there are company options */}
        {companyOptions.length > 0 && (
          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label">Select Company</label>
              <Select
                options={companyOptions}
                value={selectedCompany}
                onChange={(selected) => {
                  setSelectedCompany(selected);
                }}
                placeholder="Select Company"
              />
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
              <div className="spinner"></div>
              <p style={{ fontSize: "14px", color: "#555", margin: 0 }}>
                Loading settings...
              </p>
            </div>
          </div>
        ) : selectedCompany && !subscription && !isInTrialPeriod() ? (
          <div className="alert alert-warning" style={{ margin: "20px 0", padding: "20px", borderRadius: "8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <i className="fa fa-exclamation-triangle" style={{ fontSize: "24px", color: "#856404" }}></i>
              <div>
                <h5 style={{ margin: 0, marginBottom: "5px", color: "#856404" }}>No Active Subscription</h5>
                <p style={{ margin: 0, color: "#856404" }}>
                  This company does not have an active subscription.
                </p>
              </div>
            </div>
          </div>
        ) : (companyOptions.length === 0 || selectedCompany) ? (
          <>
            {/* 1. Trial Lesson Block */}
            {shouldDisplaySection('tester_session') && (
            <div className="card mb-4 shadow-sm">
              <div
                className="card-header bg-light border-bottom"
                onClick={() => setTrialLessonCollapsed((prev) => !prev)}
                style={{ cursor: "pointer" }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 text-dark">Tester Session</h5>
                  <div className="d-flex align-items-center gap-2">
                    {companyOptions.length > 0 && (
                      <div
                        className="form-check form-switch"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="trialLessonSwitch"
                          checked={trialLessonEnabled}
                          disabled={isToggleDisabled}
                          onChange={(e) => {
                            if (isToggleDisabled) {
                              toast.error("Please select a company", {  autoClose: false,  closeButton: true});
                              return;
                            }
                            
                            const checked = e.target.checked;
                            
                            // Show confirmation dialog
                            confirmToggleAction("Tester Session", checked, () => {
                              setTrialLessonEnabled(checked);

                              const payload = {
                                setting_type: "trial_lesson",
                                enabled: checked,
                                trial_type: trialType,
                                trial_number: trialNumber,
                                trial_fee: trialFee,
                                trial_class_values: JSON.stringify(buildTrialClassValuesPayload())
                              };

                              if (selectedCompany) {
                                payload.company_id = selectedCompany.value;
                              }

                              dispatch(SaveFeeSettings(payload)).then((response) => {
                                if (response?.payload?.code === 1) {
                                  //window.location.reload();
                                }
                              });
                            });
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                    {trialLessonEnabled && (
                    <button
                      className="btn btn-sm btn-link text-secondary p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTrialLessonCollapsed((prev) => !prev);
                      }}
                      style={{ textDecoration: "none", fontSize: "1.5rem" }}
                      type="button"
                    >
                      {trialLessonCollapsed ? "▾" : "▴"}
                    </button>
                    )}
                    {companyOptions.length === 0 && !trialLessonEnabled && (
                    <div className="alert-warning d-flex align-items-center" role="alert">
                      <i className="fa fa-exclamation-triangle me-2" style={{ fontSize: '20px' }}></i>
                      <span>Contact admin to activate this Add on</span>
                    </div>
                  )}
                  </div>
                </div>
              </div>
              {!trialLessonCollapsed && trialLessonEnabled && (
                <div className="card-body">
                  {(companyOptions.length > 0 || trialLessonEnabled) && (
                    <div className="d-flex justify-content-end mb-3">
                      <div className="form-check form-switch d-flex align-items-center">
                        <label className="form-check-label me-2" htmlFor="trialLessonInnerSwitch" style={{ fontSize: '14px', marginBottom: 0 }}>
                          Use This AddOn
                        </label>
                        <input
                          className="form-check-input m-0"
                          type="checkbox"
                          id="trialLessonInnerSwitch"
                          checked={trialLessonInnerEnabled}
                          onChange={(e) => handleTrialLessonInnerToggle(e.target.checked)}
                          style={{ 
                            width: '30px', 
                            height: '16px',
                            backgroundColor: trialLessonInnerEnabled ? '#28a745' : '#6c757d',
                            borderColor: trialLessonInnerEnabled ? '#28a745' : '#6c757d'
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {trialLessonInnerEnabled && (companyOptions.length > 0 || trialLessonEnabled) && (
                    <>
                    <div className="mb-3 d-none">
                      <label className="form-label" style={{ fontWeight: "600" }}>Trial Type</label>
                      <div>
                        <div className="form-check form-check-inline d-none">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="trialType"
                            id="trialGlobal"
                            value="global"
                            checked={trialType === "global"}
                            onChange={(e) => setTrialType(e.target.value)}
                          />
                          <label className="form-check-label" htmlFor="trialGlobal">
                            Global
                          </label>
                        </div>
                        <div className="form-check form-check-inline d-none">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="trialType"
                            id="trialPerClass"
                            value="per_class"
                            checked={trialType === "per_class"}
                            onChange={(e) => setTrialType(e.target.value)}
                          />
                          <label className="form-check-label" htmlFor="trialPerClass">
                            Per Class
                          </label>
                        </div>
                      </div>
                    </div>

                    {trialType === "global" ? (
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label">Lesson Number Allowed to Book in Trial</label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Enter number of lessons"
                            value={trialNumber}
                            onChange={(e) => setTrialNumber(e.target.value)}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Trial Fees</label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Enter trial fees"
                            value={trialFee}
                            onChange={(e) => setTrialFee(e.target.value)}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="mb-3">
                        <div className="mb-2">
                          <label className="form-label mb-0" style={{ fontWeight: "600" }}>Session-wise Settings</label>
                          <div className="text-muted" style={{ fontSize: '13px' }}>Configure how many tester sessions are allowed and the additional booking fee for each session.</div>
                        </div>
                        {trialClassList && trialClassList.length > 0 ? (
                          <div className="table-responsive">
                            <table className="table table-bordered align-middle mb-0" style={{ fontSize: '13px' }}>
                              <thead className="table-light">
                                <tr>
                                  <th style={{ minWidth: '220px' }}>Session / Venue</th>
                                  <th className="text-center" style={{ width: '70px' }}>Enable</th>
                                  <th style={{ width: '140px', whiteSpace: 'nowrap' }}>Tester Sessions Allowed <i className="fa fa-info-circle text-muted"></i></th>
                                  <th style={{ width: '140px', whiteSpace: 'nowrap' }}>Child Class Price (Normal) <i className="fa fa-info-circle text-muted"></i></th>
                                  <th style={{ width: '140px', whiteSpace: 'nowrap' }}>Adult Class Price (Normal) <i className="fa fa-info-circle text-muted"></i></th>
                                  <th style={{ width: '150px', whiteSpace: 'nowrap' }}>Tester Booking Fee (Company) <i className="fa fa-info-circle text-muted"></i></th>
                                  <th style={{ width: '170px', whiteSpace: 'nowrap' }}>Total Price to Student (Child / Adult) <i className="fa fa-info-circle text-muted"></i></th>
                                </tr>
                              </thead>
                              <tbody>
                                {trialClassList.map((classItem) => {
                                  const classSettings = trialClassValues[classItem.id] || {};
                                  const isClassEnabled = typeof classSettings.enabled !== "undefined"
                                    ? !!classSettings.enabled
                                    : typeof classSettings.free_class_available !== "undefined"
                                      ? toBoolean(classSettings.free_class_available)
                                    : deriveClassTrialToggle(classItem);
                                  const childPrice = Number(classSettings.child_price !== undefined && classSettings.child_price !== "" ? classSettings.child_price : classItem.child_price || 0);
                                  const adultPrice = Number(classSettings.adult_price !== undefined && classSettings.adult_price !== "" ? classSettings.adult_price : classItem.adult_price || 0);
                                  const bookingFee = Number(classSettings.free_class_booking_fee || 0);
                                  const childTotal = childPrice + bookingFee;
                                  const adultTotal = adultPrice + bookingFee;
                                  const totalDisplay =
                                    classItem?.class_type === "child"
                                      ? `£${childTotal.toFixed(2)}`
                                      : classItem?.class_type === "adult"
                                        ? `£${adultTotal.toFixed(2)}`
                                        : `£${childTotal.toFixed(2)} / £${adultTotal.toFixed(2)}`;
                                  return (
                                    <tr key={classItem.id}>
                                      <td style={{ fontWeight: "bold", whiteSpace: 'normal' }}>{classItem.name}</td>
                                      <td className="text-center">
                                        <div className="form-check form-switch d-inline-block">
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`trial-class-${classItem.id}`}
                                            checked={isClassEnabled}
                                            onChange={(e) => handleTrialClassToggle(classItem.id, e.target.checked)}
                                          />
                                        </div>
                                      </td>
                                      <td>
                                        <input
                                          type="number"
                                          className="form-control form-control-sm"
                                          style={{ width: '80px' }}
                                          placeholder="Sessions"
                                          disabled={!isClassEnabled}
                                          value={classSettings.no_of_free_class || ""}
                                          onChange={(e) => handleTrialClassValueChange(classItem.id, 'no_of_free_class', e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <div className="input-group input-group-sm" style={{ maxWidth: '130px' }}>
                                          <span className="input-group-text">£</span>
                                          <input
                                            type="number"
                                            className="form-control"
                                            placeholder="0.00"
                                            disabled={!isClassEnabled}
                                            value={classSettings.child_price !== undefined && classSettings.child_price !== "" ? classSettings.child_price : (classItem.child_price || "")}
                                            onChange={(e) => handleTrialClassValueChange(classItem.id, 'child_price', e.target.value)}
                                          />
                                        </div>
                                      </td>
                                      <td>
                                        <div className="input-group input-group-sm" style={{ maxWidth: '130px' }}>
                                          <span className="input-group-text">£</span>
                                          <input
                                            type="number"
                                            className="form-control"
                                            placeholder="0.00"
                                            disabled={!isClassEnabled}
                                            value={classSettings.adult_price !== undefined && classSettings.adult_price !== "" ? classSettings.adult_price : (classItem.adult_price || "")}
                                            onChange={(e) => handleTrialClassValueChange(classItem.id, 'adult_price', e.target.value)}
                                          />
                                        </div>
                                      </td>
                                      <td>
                                        <div className="input-group input-group-sm" style={{ maxWidth: '130px' }}>
                                          <span className="input-group-text">£</span>
                                          <input
                                            type="number"
                                            className="form-control"
                                            placeholder="0.00"
                                            disabled={!isClassEnabled}
                                            value={classSettings.free_class_booking_fee || ""}
                                            onChange={(e) => handleTrialClassValueChange(classItem.id, 'free_class_booking_fee', e.target.value)}
                                          />
                                        </div>
                                      </td>
                                      <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{totalDisplay}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-muted">No Session Found.</p>
                        )}
                      </div>
                    )}

                    {(trialClassList && trialClassList.length > 0) && (
                      <button
                        className="btn btn-save p-2 fs-6"
                        style={{ padding: '1rem', height: '50px', borderRadius: '8px', width: '10%' }}
                        onClick={handleSaveTrialLesson}
                      >
                        Save
                      </button>
                    )}
                    </>
                  )}

                </div>
              )}
            </div>
            )}

            {shouldDisplaySection('membership') && (
            <div className="card mb-4 shadow-sm">
              <div
                className="card-header bg-light border-bottom"
                onClick={() => setFreeMembershipCollapsed((prev) => !prev)}
                style={{ cursor: "pointer" }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 text-dark">Memberships</h5>
                  <div className="d-flex align-items-center gap-2">
                    {companyOptions.length > 0 && (
                      <div
                        className="form-check form-switch"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="freeMembershipSwitch"
                          checked={allowFreeMembership}
                          disabled={isToggleDisabled}
                          onChange={(e) => handleFreeMembershipToggle(e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                    {allowFreeMembership && (
                    <button
                      className="btn btn-sm btn-link text-secondary p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFreeMembershipCollapsed((prev) => !prev);
                      }}
                      style={{ textDecoration: "none", fontSize: "1.5rem" }}
                      type="button"
                    >
                      {freeMembershipCollapsed ? "▾" : "▴"}
                    </button>
                    )}
                     {companyOptions.length === 0 && !allowFreeMembership && (
                    <div className="alert-warning d-flex align-items-center" role="alert">
                      <i className="fa fa-exclamation-triangle me-2" style={{ fontSize: '20px' }}></i>
                      <span>Contact admin to activate this Add on</span>
                    </div>
                  )}
                  </div>
                </div>
              </div>
              {!freeMembershipCollapsed && allowFreeMembership && (
                <div className="card-body">
                  {(companyOptions.length > 0 || allowFreeMembership) && (
                    <div className="d-flex justify-content-end mb-3">
                      <div className="form-check form-switch d-flex align-items-center">
                        <label className="form-check-label me-2" htmlFor="membershipInnerSwitch" style={{ fontSize: '14px', marginBottom: 0 }}>
                          Use This AddOn
                        </label>
                        <input
                          className="form-check-input m-0"
                          type="checkbox"
                          id="membershipInnerSwitch"
                          checked={allowFreeMembershipInner}
                          onChange={(e) => handleFreeMembershipInnerToggle(e.target.checked)}
                          style={{ 
                            width: '30px', 
                            height: '16px',
                            backgroundColor: allowFreeMembershipInner ? '#28a745' : '#6c757d',
                            borderColor: allowFreeMembershipInner ? '#28a745' : '#6c757d'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {/* 3. Events Block */}
            {shouldDisplaySection('event') && (
            <div className="card mb-4 shadow-sm">
              <div
                className="card-header bg-light border-bottom"
                onClick={() => setEventsCollapsed((prev) => !prev)}
                style={{ cursor: "pointer" }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 text-dark">Events</h5>
                  <div className="d-flex align-items-center gap-2">
                    {companyOptions.length > 0 && (
                      <div
                        className="form-check form-switch"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="eventsSwitch"
                          checked={eventsEnabled}
                          disabled={isToggleDisabled}
                          onChange={(e) => handleEventsToggle(e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                    {eventsEnabled && (
                    <button
                      className="btn btn-sm btn-link text-secondary p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEventsCollapsed((prev) => !prev);
                      }}
                      style={{ textDecoration: "none", fontSize: "1.5rem" }}
                      type="button"
                    >
                      {eventsCollapsed ? "▾" : "▴"}
                    </button>
                    )}  
                    {companyOptions.length === 0 && !eventsEnabled && (
                    <div className="alert-warning d-flex align-items-center" role="alert">
                      <i className="fa fa-exclamation-triangle me-2" style={{ fontSize: '20px' }}></i>
                      <span>Contact admin to activate this Add on</span>
                    </div>
                  )}
                  </div>
                </div>
              </div>
              {!eventsCollapsed && eventsEnabled && (
                <div className="card-body">
                  {(companyOptions.length > 0 || eventsEnabled) && (
                    <div className="d-flex justify-content-end mb-3">
                      <div className="form-check form-switch d-flex align-items-center">
                        <label className="form-check-label me-2" htmlFor="eventsInnerSwitch" style={{ fontSize: '14px', marginBottom: 0 }}>
                          Use This AddOn
                        </label>
                        <input
                          className="form-check-input m-0"
                          type="checkbox"
                          id="eventsInnerSwitch"
                          checked={eventsInnerEnabled}
                          onChange={(e) => handleEventsInnerToggle(e.target.checked)}
                          style={{ 
                            width: '30px', 
                            height: '16px',
                            backgroundColor: eventsInnerEnabled ? '#28a745' : '#6c757d',
                            borderColor: eventsInnerEnabled ? '#28a745' : '#6c757d'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {/* 4. Finance Block */}
            {shouldDisplaySection('finance') && (
            <div className="card mb-4 shadow-sm">
              <div
                className="card-header bg-light border-bottom"
                onClick={() => setFinanceCollapsed((prev) => !prev)}
                style={{ cursor: "pointer" }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 text-dark">Finance</h5>
                  <div className="d-flex align-items-center gap-2">
                    {companyOptions.length > 0 && (
                      <div
                        className="form-check form-switch"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="financeSwitch"
                          checked={financeEnabled}
                          disabled={isToggleDisabled}
                          onChange={(e) => handleFinanceToggle(e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                    {financeEnabled && (
                    <button
                      className="btn btn-sm btn-link text-secondary p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFinanceCollapsed((prev) => !prev);
                      }}
                      style={{ textDecoration: "none", fontSize: "1.5rem" }}
                      type="button"
                    >
                      {financeCollapsed ? "▾" : "▴"}
                    </button>
                    )}  
                    {companyOptions.length === 0 && !financeEnabled && (
                    <div className="alert-warning d-flex align-items-center" role="alert">
                      <i className="fa fa-exclamation-triangle me-2" style={{ fontSize: '20px' }}></i>
                      <span>Contact admin to activate this Add on</span>
                    </div>
                  )}
                  </div>
                </div>
              </div>
              {!financeCollapsed && financeEnabled && (
                <div className="card-body">
                  {(companyOptions.length > 0 || financeEnabled) && (
                    <div className="d-flex justify-content-end mb-3">
                      <div className="form-check form-switch d-flex align-items-center">
                        <label className="form-check-label me-2" htmlFor="financeInnerSwitch" style={{ fontSize: '14px', marginBottom: 0 }}>
                          Use This AddOn
                        </label>
                        <input
                          className="form-check-input m-0"
                          type="checkbox"
                          id="financeInnerSwitch"
                          checked={financeInnerEnabled}
                          onChange={(e) => handleFinanceInnerToggle(e.target.checked)}
                          style={{ 
                            width: '30px', 
                            height: '16px',
                            backgroundColor: financeInnerEnabled ? '#28a745' : '#6c757d',
                            borderColor: financeInnerEnabled ? '#28a745' : '#6c757d'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {/* 5. Admin Commission Block */}
            {shouldDisplaySection('service_fee') && (
            <div className="card mb-4 shadow-sm">
              <div
                className="card-header bg-light border-bottom"
                onClick={() => setAdminCommissionCollapsed((prev) => !prev)}
                style={{ cursor: "pointer" }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 text-dark">Service Fee</h5>
                  <div className="d-flex align-items-center gap-2">
                    {companyOptions.length > 0 && (
                      <div
                        className="form-check form-switch"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="adminCommissionSwitch"
                          checked={adminCommissionEnabled}
                          disabled={isToggleDisabled}
                          onChange={(e) => handleAdminCommissionToggle(e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                    {adminCommissionEnabled && (
                    <button
                      className="btn btn-sm btn-link text-secondary p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAdminCommissionCollapsed((prev) => !prev);
                      }}
                      style={{ textDecoration: "none", fontSize: "1.5rem" }}
                      type="button"
                    >
                      {adminCommissionCollapsed ? "▾" : "▴"}
                    </button>
                    )}
                    {companyOptions.length === 0 && !adminCommissionEnabled && (
                      <div className="alert-warning d-flex align-items-center" role="alert">
                        <i className="fa fa-exclamation-triangle me-2" style={{ fontSize: '20px' }}></i>
                        <span>Contact admin to activate this Add on</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {!adminCommissionCollapsed && adminCommissionEnabled && (
                <div className="card-body">
                  <div className="d-flex justify-content-end mb-3">
                    <div className="form-check form-switch d-flex align-items-center">
                      <label className="form-check-label me-2" htmlFor="adminCommissionInnerSwitch" style={{ fontSize: '14px', marginBottom: 0 }}>
                        Use This AddOn
                      </label>
                      <input
                        className="form-check-input m-0"
                        type="checkbox"
                        id="adminCommissionInnerSwitch"
                        checked={adminCommissionInnerEnabled}
                        onChange={(e) => handleAdminCommissionInnerToggle(e.target.checked)}
                        style={{ 
                          width: '30px', 
                          height: '16px',
                          backgroundColor: adminCommissionInnerEnabled ? '#28a745' : '#6c757d',
                          borderColor: adminCommissionInnerEnabled ? '#28a745' : '#6c757d'
                        }}
                      />
                    </div>
                  </div>
                  {adminCommissionInnerEnabled && (
                    <>
                    <div className="mb-3">
                      <label className="form-label">Service Fee (%)</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter Service Fee percentage"
                        value={adminCommissionValue}
                        onChange={(e) => setAdminCommissionValue(e.target.value)}
                      />
                    </div>

                      <button className="btn btn-save p-2 fs-6" style={{ padding: '1rem', height: '50px', borderRadius: '8px', width: '10%' }} onClick={handleSaveAdminCommission}>
                        Save
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            )}

            {/* 5. Late Booking Block */}
            {shouldDisplaySection('late_booking') && (
            <div className="card mb-4 shadow-sm">
              <div
                className="card-header bg-light border-bottom"
                onClick={() => setLateBookingCollapsed((prev) => !prev)}
                style={{ cursor: "pointer" }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 text-dark">Late Booking</h5>
                  <div className="d-flex align-items-center gap-2">
                    {companyOptions.length > 0 && (
                      <div
                        className="form-check form-switch"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="lateBookingSwitch"
                          checked={lateBookingEnabled}
                          disabled={isToggleDisabled}
                          onChange={(e) => handleLateBookingToggle(e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                    {lateBookingEnabled && (
                    <button
                      className="btn btn-sm btn-link text-secondary p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLateBookingCollapsed((prev) => !prev);
                      }}
                      style={{ textDecoration: "none", fontSize: "1.5rem" }}
                      type="button"
                    >
                      {lateBookingCollapsed ? "▾" : "▴"}
                    </button>
                    )}
                    {companyOptions.length === 0 && !lateBookingEnabled && (
                    <div className="alert-warning d-flex align-items-center" role="alert">
                      <i className="fa fa-exclamation-triangle me-2" style={{ fontSize: '20px' }}></i>
                      <span>Contact admin to activate this Add on</span>
                    </div>
                  )}
                  </div>
                </div>
              </div>
              {!lateBookingCollapsed && lateBookingEnabled && (
                <div className="card-body">
                  <div className="d-flex justify-content-end mb-3">
                    <div className="form-check form-switch d-flex align-items-center">
                      <label className="form-check-label me-2" htmlFor="lateBookingInnerSwitch" style={{ fontSize: '14px', marginBottom: 0 }}>
                        Use This AddOn
                      </label>
                      <input
                        className="form-check-input m-0"
                        type="checkbox"
                        id="lateBookingInnerSwitch"
                        checked={lateBookingInnerEnabled}
                        onChange={(e) => handleLateBookingInnerToggle(e.target.checked)}
                        style={{ 
                          width: '30px', 
                          height: '16px',
                          backgroundColor: lateBookingInnerEnabled ? '#28a745' : '#6c757d',
                          borderColor: lateBookingInnerEnabled ? '#28a745' : '#6c757d'
                        }}
                      />
                    </div>
                  </div>
                  {lateBookingInnerEnabled && (
                    <>
                    <div className="mb-3">
                      <label className="form-label" style={{ fontWeight: "600" }}>Late Booking Charge Type</label>
                      <div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="lateBookingChargeType"
                            id="lateBookingGlobal"
                            value="global"
                            checked={lateBookingChargeType === "global"}
                            onChange={(e) => setLateBookingChargeType(e.target.value)}
                          />
                          <label className="form-check-label" htmlFor="lateBookingGlobal">
                            Global
                          </label>
                        </div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="lateBookingChargeType"
                            id="lateBookingPerClass"
                            value="per_class"
                            checked={lateBookingChargeType === "per_class"}
                            onChange={(e) => setLateBookingChargeType(e.target.value)}
                          />
                          <label className="form-check-label" htmlFor="lateBookingPerClass">
                            Per Session
                          </label>
                        </div>
                      </div>
                    </div>

                    {lateBookingChargeType === "global" ? (
                      <div className="mb-3">
                        <div className="row">
                          <div className="col-md-12">
                            <label className="form-label">How Much Time/Hours Before User Can Book Class</label>
                            <input
                              type="number"
                              className="form-control"
                              placeholder="Enter hours"
                              value={lateBookingHours}
                              onChange={(e) => setLateBookingHours(e.target.value)}
                            />
                          </div>
                          <div className="col-md-6 d-none">
                            <label className="form-label">Late Booking Charge</label>
                            <input
                              type="number"
                              className="form-control"
                              placeholder="Enter charge amount"
                              value={lateBookingCharge}
                              onChange={(e) => setLateBookingCharge(e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-3">
                        <label className="form-label">Session-wise Late Booking Settings</label>
                        {classList && classList.length > 0 ? (
                          classList.map((classItem) => (
                            <div key={classItem.id} className="row mb-2">
                              <div className="col-md-3">
                                <label className="form-label" style={{ fontSize: "13px", fontWeight: "600" }}>{classItem.name}</label>
                              </div>
                              <div className="col-md-9">
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  placeholder="Hours before booking"
                                  value={lateBookingClassValues[classItem.id]?.late_booking_hours || ""}
                                  onChange={(e) => handleLateBookingClassValueChange(classItem.id, 'late_booking_hours', e.target.value)}
                                />
                              </div>
                              <div className="col-md-5 d-none">
                                <input
                                  type="number"
                                  className="form-control"
                                  placeholder="Late booking charge"
                                  value={lateBookingClassValues[classItem.id]?.late_booking_charge || ""}
                                  onChange={(e) => handleLateBookingClassValueChange(classItem.id, 'late_booking_charge', e.target.value)}
                                />
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-muted">No Session found.</p>
                        )}
                      </div>
                    )}
                    {(lateBookingChargeType === "global" || (classList && classList.length > 0)) && (
                      <button className="btn btn-save p-2 fs-6" style={{ padding: '1rem', height: '50px', borderRadius: '8px', width: '10%' }} onClick={handleSaveLateBooking}>
                        Save
                      </button>
                    )}
                    </>
                  )}
                </div>
              )}
            </div>
            )}

            {/* 6. Hall Fee Block */}
            {shouldDisplaySection('hall_fee') && (
            <div className="card mb-4 shadow-sm">
              <div
                className="card-header bg-light border-bottom"
                onClick={() => setHallFeeCollapsed((prev) => !prev)}
                style={{ cursor: "pointer" }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 text-dark">Hall Fee</h5>
                  <div className="d-flex align-items-center gap-2">
                    {companyOptions.length > 0 && (
                      <div
                        className="form-check form-switch"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="hallFeeSwitch"
                          checked={hallFeeEnabled}
                          disabled={isToggleDisabled}
                          onChange={(e) => handleHallFeeToggle(e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                    {hallFeeEnabled && (
                    <button
                      className="btn btn-sm btn-link text-secondary p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setHallFeeCollapsed((prev) => !prev);
                      }}
                      style={{ textDecoration: "none", fontSize: "1.5rem" }}
                      type="button"
                    >
                      {hallFeeCollapsed ? "▾" : "▴"}
                    </button>
                    )}
                    {companyOptions.length === 0 && !hallFeeEnabled && (
                    <div className="alert-warning d-flex align-items-center" role="alert">
                      <i className="fa fa-exclamation-triangle me-2" style={{ fontSize: '20px' }}></i>
                      <span>Contact admin to activate this Add on</span>
                    </div>
                  )}
                  </div>
                </div>
              </div>
              {!hallFeeCollapsed && hallFeeEnabled && (
                <div className="card-body">
                  <div className="d-flex justify-content-end mb-3">
                    <div className="form-check form-switch d-flex align-items-center">
                      <label className="form-check-label me-2" htmlFor="hallFeeInnerSwitch" style={{ fontSize: '14px', marginBottom: 0 }}>
                        Use This AddOn
                      </label>
                      <input
                        className="form-check-input m-0"
                        type="checkbox"
                        id="hallFeeInnerSwitch"
                        checked={hallFeeInnerEnabled}
                        onChange={(e) => handleHallFeeInnerToggle(e.target.checked)}
                        style={{ 
                          width: '30px', 
                          height: '16px',
                          backgroundColor: hallFeeInnerEnabled ? '#28a745' : '#6c757d',
                          borderColor: hallFeeInnerEnabled ? '#28a745' : '#6c757d'
                        }}
                      />
                    </div>
                  </div>
                  {hallFeeInnerEnabled && (
                    <>
                    <div className="mb-3">
                      <label className="form-label" style={{ fontWeight: "600" }}>Fee Type</label>
                      <div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="hallFeeType"
                            id="hallFeeGlobal"
                            value="global"
                            checked={hallFeeType === "global"}
                            onChange={(e) => setHallFeeType(e.target.value)}
                          />
                          <label className="form-check-label" htmlFor="hallFeeGlobal">
                            Global
                          </label>
                        </div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="hallFeeType"
                            id="hallFeeVenue"
                            value="venue"
                            checked={hallFeeType === "venue"}
                            onChange={(e) => setHallFeeType(e.target.value)}
                          />
                          <label className="form-check-label" htmlFor="hallFeeVenue">
                            Per Location
                          </label>
                        </div>
                      </div>
                    </div>

                    {hallFeeType === "global" ? (
                      <div className="mb-3">
                        <label className="form-label">Global Hall Fee Value</label>
                        <div className="row">
                          <div className="col-md-6">
                            <input
                              type="number"
                              className="form-control"
                              placeholder="Enter Hall Fee per Hour"
                              value={hallFeeGlobalPerHour}
                              onChange={(e) => setHallFeeGlobalPerHour(e.target.value)}
                            />
                          </div>
                          <div className="col-md-6">
                            <input
                              type="number"
                              className="form-control"
                              placeholder="Enter Hall Fee per Day"
                              value={hallFeeGlobalPerDay}
                              onChange={(e) => setHallFeeGlobalPerDay(e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-3">
                        <label className="form-label">Location-wise Hall Fee</label>
                        {venues && venues.length > 0 ? (
                          venues.map((venue) => (
                            <div key={venue.id} className="row mb-2">
                              <div className="col-md-2">
                                <label className="form-label" style={{ fontSize: "13px", fontWeight: "600" }}>{venue.name}</label>
                              </div>
                              <div className="col-md-5">
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  placeholder="Enter Hall Fee per Hour"
                                  value={hallFeeVenueValues[venue.id]?.per_hour || venue.hall_fees_per_hour}
                                  onChange={(e) => handleVenueValueChange(venue.id, 'per_hour', e.target.value)}
                                />
                              </div>
                              <div className="col-md-5">
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  placeholder="Enter Hall Fee per Day"
                                  value={hallFeeVenueValues[venue.id]?.per_day || venue.hall_fees_per_day}
                                  onChange={(e) => handleVenueValueChange(venue.id, 'per_day', e.target.value)}
                                />
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-muted">No Location Found.</p>
                        )}
                      </div>
                    )}
                    {(venues && venues.length > 0) && (
                      <button className="btn btn-save p-2 fs-6" style={{ padding: '1rem', height: '50px', borderRadius: '8px', width: '10%' }} onClick={handleSaveHallFee}>
                        Save
                      </button>
                    )}
                  </>
                  )}
                </div>
              )}
            </div>
            )}

            {/* 7. Franchise Fee Block */}
            {shouldDisplaySection('franchise_fee') && (
            <div className="card mb-4 shadow-sm">
              <div
                className="card-header bg-light border-bottom"
                onClick={() => setFranchiseFeeCollapsed((prev) => !prev)}
                style={{ cursor: "pointer" }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 text-dark">Franchise Fee</h5>
                  <div className="d-flex align-items-center gap-2">
                    {companyOptions.length > 0 && (
                      <div
                        className="form-check form-switch"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="franchiseFeeSwitch"
                          checked={franchiseFeeEnabled}
                          disabled={isToggleDisabled}
                          onChange={(e) => handleFranchiseFeeToggle(e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                    {franchiseFeeEnabled && (
                    <button
                      className="btn btn-sm btn-link text-secondary p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFranchiseFeeCollapsed((prev) => !prev);
                      }}
                      style={{ textDecoration: "none", fontSize: "1.5rem" }}
                      type="button"
                    >
                      {franchiseFeeCollapsed ? "▾" : "▴"}
                    </button>
                    )}
                    {companyOptions.length === 0 && !franchiseFeeEnabled && (
                    <div className="alert-warning d-flex align-items-center" role="alert">
                      <i className="fa fa-exclamation-triangle me-2" style={{ fontSize: '20px' }}></i>
                      <span>Contact admin to activate this Add on</span>
                    </div>
                  )}
                  </div>
                </div>
              </div>
              {!franchiseFeeCollapsed && franchiseFeeEnabled && (
                <div className="card-body">
                  <div className="d-flex justify-content-end mb-3">
                    <div className="form-check form-switch d-flex align-items-center">
                      <label className="form-check-label me-2" htmlFor="franchiseFeeInnerSwitch" style={{ fontSize: '14px', marginBottom: 0 }}>
                        Use This AddOn
                      </label>
                      <input
                        className="form-check-input m-0"
                        type="checkbox"
                        id="franchiseFeeInnerSwitch"
                        checked={franchiseFeeInnerEnabled}
                        onChange={(e) => handleFranchiseFeeInnerToggle(e.target.checked)}
                        style={{ 
                          width: '30px', 
                          height: '16px',
                          backgroundColor: franchiseFeeInnerEnabled ? '#28a745' : '#6c757d',
                          borderColor: franchiseFeeInnerEnabled ? '#28a745' : '#6c757d'
                        }}
                      />
                    </div>
                  </div>
                  {franchiseFeeInnerEnabled && (
                    <>
                    <div className="mb-3">
                      <label className="form-label" style={{ fontWeight: "600" }}>Franchise Fee Type</label>
                      <div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="franchiseFeeType"
                            id="franchiseFeeGlobal"
                            value="global"
                            checked={franchiseFeeType === "global"}
                            onChange={(e) => setFranchiseFeeType(e.target.value)}
                          />
                          <label className="form-check-label" htmlFor="franchiseFeeGlobal">
                            Global
                          </label>
                        </div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="franchiseFeeType"
                            id="franchiseFeePerSession"
                            value="per_session"
                            checked={franchiseFeeType === "per_session"}
                            onChange={(e) => setFranchiseFeeType(e.target.value)}
                          />
                          <label className="form-check-label" htmlFor="franchiseFeePerSession">
                            Per Session
                          </label>
                        </div>
                      </div>
                    </div>

                    {franchiseFeeType === "global" ? (
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label">Student Threshold</label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Enter student threshold"
                            value={studentThreshold}
                            onChange={(e) => setStudentThreshold(e.target.value)}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Franchise Fee</label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Enter franchise fee"
                            value={franchiseFeeValue}
                            onChange={(e) => setFranchiseFeeValue(e.target.value)}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="mb-3">
                        <label className="form-label">Session-wise Franchise Fee Settings</label>
                        {franchiseFeeClassList && franchiseFeeClassList.length > 0 ? (
                          franchiseFeeClassList.map((classItem) => (
                            <div key={classItem.id} className="row mb-2">
                              <div className="col-md-3">
                                <label className="form-label" style={{ fontSize: "13px", fontWeight: "600" }}>{classItem.name}</label>
                              </div>
                              <div className="col-md-4">
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  placeholder="Student threshold"
                                  value={franchiseFeeClassValues[classItem.id]?.student_threshold || ""}
                                  onChange={(e) => handleFranchiseFeeClassValueChange(classItem.id, 'student_threshold', e.target.value)}
                                />
                              </div>
                              <div className="col-md-5">
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  placeholder="Franchise fee"
                                  value={franchiseFeeClassValues[classItem.id]?.franchise_fee || ""}
                                  onChange={(e) => handleFranchiseFeeClassValueChange(classItem.id, 'franchise_fee', e.target.value)}
                                />
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-muted">No Session found.</p>
                        )}
                      </div>
                    )}
                      <button className="btn btn-save p-2 fs-6" style={{ padding: '1rem', height: '50px', borderRadius: '8px', width: '10%' }} onClick={handleSaveFranchiseFee}>
                        Save
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            )}

            
          </>
        ) : companyOptions.length > 0 ? (
          <div className="alert alert-info">
            Please select a company to view and edit settings.
          </div>
        ) : null}
      </div>

      {/* Beautiful Confirmation Modal */}
      {showConfirmModal && (
        <div 
          className="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={handleConfirmNo}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content" style={{ borderRadius: '15px', overflow: 'hidden' }}>
              <div className="modal-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <h5 className="modal-title" style={{ fontWeight: '600', color: '#333' }}>
                  <span style={{ fontSize: '24px', marginRight: '10px' }}>⚠️</span>
                  Confirm Action
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleConfirmNo}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body" style={{ padding: '30px', fontSize: '16px', color: '#555' }}>
                <p style={{ marginBottom: '0', lineHeight: '1.6' }}>
                  Are you sure you want to <strong style={{ color: confirmModalData.action === 'enable' ? '#28a745' : '#dc3545' }}>{confirmModalData.action}</strong> <strong>{confirmModalData.sectionName}</strong>?
                </p>
              </div>
              <div className="modal-footer" style={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #dee2e6', padding: '15px 30px' }}>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleConfirmNo}
                  style={{ 
                    padding: '10px 25px', 
                    borderRadius: '8px',
                    fontWeight: '500',
                    minWidth: '100px'
                  }}
                >
                  No
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={handleConfirmYes}
                  style={{ 
                    padding: '10px 25px', 
                    borderRadius: '8px',
                    fontWeight: '500',
                    minWidth: '100px',
                    backgroundColor: '#007bff',
                    borderColor: '#007bff'
                  }}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

export default FeeSettings;
